import React from "react";

/**
 *  UI: border magic from tailwind css btns
 *  Link: https://ui.aceternity.com/components/tailwindcss-buttons
 *
 *  change border radius to rounded-lg
 *  add margin of md:mt-10
 *  remove focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50
 */
const MagicButton = ({
	title,
	icon,
	position,
	handleClick,
	/* DEFAULTED, BECAUSE AN UNDEFAULTED OPTIONAL PROP IS A STRING IN THE
	   MARKUP. This is interpolated into the className below, and the one
	   call site that omits it — the hero's "Explore my Projects" button —
	   shipped the literal `undefined` as a class token in build/index.html.
	   Harmless to paint, since no such class exists, and still wrong: it is
	   in the HTML a search engine reads, and it is indistinguishable from a
	   class name that was meant to be there and got lost (D-093). */
	otherClasses = ""
}: {
	title: string;
	icon: React.ReactNode;
	position: string;
	handleClick?: () => void;
	otherClasses?: string;
}) => {
	/* `md:w-60` and `md:mt-10` used to live on the button below, and both were
	   the HERO's layout decisions baked into a shared control (D-080). The
	   margin put 40px of space above this button at >=768 only, so beside the
	   Resume button it sat 40px low at 768 and overlapped at 1280 — measured
	   tops 858 vs 879, a -41px gap. Width and spacing now belong to whatever
	   lays this button out; `w-full` means "fill the box I am given". */
	return (
		<button
			className="relative inline-flex h-12 w-full overflow-hidden rounded-lg p-[1px] focus:outline-none"
			onClick={handleClick}
		>
			{/* The rotating conic border. Its two stops are tokens (D-035): in dark
			    they are the lilac/indigo pair this button has always spun, and in
			    light they drop to violet/fuchsia so the ring stays visible against
			    a near-white face instead of washing out. */}
			<span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,var(--grad-spin-a)_0%,var(--grad-spin-b)_50%,var(--grad-spin-a)_100%)]" />

			{/* remove px-3 py-1, add px-5 gap-2 */}
			<span
				className={`inline-flex h-full w-full cursor-pointer items-center justify-center rounded-lg
			      bg-[var(--bg-elevated)] px-7 text-sm font-medium text-[var(--text-strong)] backdrop-blur-3xl gap-2 ${otherClasses}`}
			>
				{position === "left" && icon}
				{title}
				{position === "right" && icon}
			</span>
		</button>
	);
};

export default MagicButton;
